import * as React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { convertTableBoolean } from '../../utils/convertTableValues';
import { parseApiDateTime } from '../../utils/parseApiDateTime';
import DashboardLayout from '../../layouts/DashboardLayout';
import ActionsPanel from '../../organisms/ActionsPanel';
import useTablePage from '../../hooks/useTablePage';
import {
  getSpecialPermissionList,
  createSpecialPermission,
  updateSpecialPermission,
  deleteSpecialPermission,
  SpecialPermissionType,
} from '../../api/specialPermission';
import SpecialPermissionForm from '../../forms/SpecialPermissionForm';
import Table from '../../organisms/Table';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import TruncatedTextCell from '../../molecules/TruncatedTextCell';

const columnsOrder = [
  'name',
  'permissionType',
  'description',
  'assignedUserDisplay',
  'isActive',
  'updatedAt',
];

/**
 * Settings > Special Permission (EA-1343).
 *
 * A new row is inert until code reads its key, which is deliberate -- it lets
 * a permission be staged ahead of the feature that will use it. The server
 * derives the key from the name and refuses to delete the rows an automation
 * depends on; deleting one of those would leave it permanently unassigned
 * with nothing on screen to explain why. Untick Active instead.
 */
const SpecialPermissionPage = () => {
  const {
    setSearch,
    handleAdd,
    search,
    tableManuallyUpdate,
    isModalOpen,
    values,
    handleEdit,
    handleDelete,
    errors,
    createOrUpdate,
    selectedItem,
    setSelectedItem,
    updateTable,
    showErrorModal,
    showSuccessModal,
    handleCloseModal,
    handleCloseModalInfo,
    modalError,
    modalErrorTitle,
    modalSuccess,
    modalTitle,
  } = useTablePage<SpecialPermissionType>({
    createEndpoint: createSpecialPermission,
    updateEndpoint: updateSpecialPermission,
    modalCreateTitle: 'Add Special Permission',
    modalUpdateTitle: 'Edit Special Permission',
  });

  const [deleteError, setDeleteError] = React.useState('');

  /**
   * Deliberately not useTablePage's `deleteById`: its catch block is
   * commented out, so a refusal would close the dialog and show nothing.
   * The server rejects deleting a permission an automation depends on, and
   * that reason has to reach the admin.
   */
  const confirmDelete = (permission: SpecialPermissionType) =>
    deleteSpecialPermission(permission.id)
      .then(() => {
        setSelectedItem(null);
        updateTable();
        showSuccessModal(true);
      })
      .catch(error => {
        const data = error?.response?.data;
        const reason =
          data?.nonFieldErrors?.[0] ||
          data?.detail ||
          'This permission could not be deleted.';
        setSelectedItem(null);
        setDeleteError(reason);
        showErrorModal(true);
      });

  const columns = column => {
    switch (column) {
      case 'name':
        return 'Permission Name';
      case 'permissionType':
        return 'Type';
      case 'description':
        return 'Description';
      case 'assignedUserDisplay':
        return 'Assigned User / Email';
      case 'isActive':
        return 'Status';
      case 'updatedAt':
        return 'Last Updated';
      default:
        // id, key, assignedUser, isDeleted
        return null;
    }
  };

  const formatCell = (value: any, column?: string) => {
    if (column === 'description') {
      return <TruncatedTextCell value={value} />;
    }
    if (column === 'isActive') {
      // Ahead of convertTableBoolean, which would render a bare "Yes"/"No".
      return (
        <StatusPill $active={!!value}>
          {value ? 'Active' : 'Inactive'}
        </StatusPill>
      );
    }
    if (column === 'updatedAt') {
      // DRF renders "09/21/2026, 09:24:15 +0000"; show the date and time
      // without the seconds and offset. parseApiDateTime exists because
      // date-fns cannot parse that format directly.
      const parsed = parseApiDateTime(value);
      return parsed ? format(parsed, 'MM/dd/yyyy h:mm a') : value;
    }
    return convertTableBoolean(value);
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  return (
    <DashboardLayout>
      <PageWrapper>
        <Intro>
          <Title>Special Permission</Title>
          <Subtitle>
            Configure users for specific system permissions
            and automated assignments.
          </Subtitle>
        </Intro>

        <ActionsPanel onSearch={setSearch} onAdd={handleAdd} />

        <Container>
          <Table
            customSearch
            searchQuery={search}
            columns={columns}
            columnsOrder={columnsOrder}
            rows={getSpecialPermissionList}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatValue={formatCell}
            manuallyUpdate={tableManuallyUpdate}
          />
        </Container>

        {isModalOpen && (
          <ModalDefaultDraggable
            onClose={handleCloseForm}
            title={modalTitle}
          >
            <SpecialPermissionForm
              initialValues={values}
              initialErrors={errors}
              modalError={modalErrorTitle.length > 0}
              onSubmit={createOrUpdate}
            />
          </ModalDefaultDraggable>
        )}

        {selectedItem && (
          <ModalDeleteConfirmation
            onCancel={() => setSelectedItem(null)}
            onConfirm={() => confirmDelete(selectedItem)}
          >
            <strong>{selectedItem.name}</strong>
          </ModalDeleteConfirmation>
        )}

        {(modalSuccess || modalError) && (
          <ModalInfo
            timeToClose={5000}
            isError={modalError}
            onClose={() => {
              setDeleteError('');
              handleCloseModalInfo();
            }}
            errorMsg={deleteError || modalErrorTitle}
          />
        )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Intro = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

/**
 * Status chip for the Active column.
 *
 * Solid green rather than the pale fill `molecules/EboxStatus`'s LiveBadge
 * uses, so "Active" reads as green at 12px. Note the pairing is load-bearing:
 * --success-600 behind white is 4.58:1, which clears WCAG AA, while the
 * greener-looking --success-600 *text* on a --success-50 fill is only 4.11:1
 * and fails. Don't "simplify" this back to a light fill with green text.
 *
 * Inactive stays neutral rather than red -- a paused permission is an ordinary
 * configuration state, not an error.
 */
const StatusPill = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $active }) =>
    ($active ? 'var(--success-600)' : 'var(--neutral-100)')};
  color: ${({ $active }) =>
    ($active ? 'var(--neutral-0)' : 'var(--neutral-500)')};
`;

export default SpecialPermissionPage;
